import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { calendarFor, finishVarianceWorkingDays } from '../../lib/calendar'
import { formatDays, formatIsoDate } from '../../lib/format'
import type { ScheduleActivity, ScheduleCalendar } from '../../types'
import HealthBadge from '../../components/HealthBadge'
import FieldRow from './FieldRow'

interface Props {
  activity: ScheduleActivity
  /** Review fix (Important #3): needed to compute the baseline finish variance in working days,
   * matching `ScheduleStats.finishVarianceDays` and the timeline tooltip. */
  calendarsById: Map<string, ScheduleCalendar>
  defaultCalendarId: string
}

/**
 * Read-only computed fields of the activity panel (Task 17, spec B §9.7 step 4) — nothing here
 * mutates, so `FieldRow`'s `pending`/`error` props are never used. A summary's rolled percent lands
 * here (not in `ProgressSection`, which isn't mounted for one) since it's a rollup, not an input.
 */
const DatesSection: React.FC<Props> = ({ activity, calendarsById, defaultCalendarId }) => {
  const { t } = useTranslation()
  // Review fix (Important #3): working days, not calendar days — matches the backend's
  // `ScheduleStats.finishVarianceDays` and the timeline tooltip.
  const cal = calendarFor(activity, calendarsById, defaultCalendarId)
  const variance = activity.baselineFinish && cal ? finishVarianceWorkingDays(cal, activity.baselineFinish, activity.finish) : null

  return (
    <div>
      <FieldRow label={t('Schedule_col_start')}><span className="text-sm tabular-nums">{formatIsoDate(t, activity.start)}</span></FieldRow>
      <FieldRow label={t('Schedule_col_finish')}><span className="text-sm tabular-nums">{formatIsoDate(t, activity.finish)}</span></FieldRow>
      <FieldRow label={t('Schedule_field_late_start')}><span className="text-sm tabular-nums">{formatIsoDate(t, activity.lateStart)}</span></FieldRow>
      <FieldRow label={t('Schedule_field_late_finish')}><span className="text-sm tabular-nums">{formatIsoDate(t, activity.lateFinish)}</span></FieldRow>
      <FieldRow label={t('Schedule_col_float')}><span className="text-sm tabular-nums">{formatDays(t, activity.totalFloatDays)}</span></FieldRow>
      {activity.isSummary && (
        <FieldRow label={t('Schedule_field_percent')}><span className="text-sm tabular-nums">{activity.rolledPercent}%</span></FieldRow>
      )}
      {activity.baselineStart && (
        <FieldRow label={t('Schedule_field_baseline_start')}><span className="text-sm tabular-nums">{formatIsoDate(t, activity.baselineStart)}</span></FieldRow>
      )}
      {activity.baselineFinish && (
        <FieldRow label={t('Schedule_field_baseline_finish')}><span className="text-sm tabular-nums">{formatIsoDate(t, activity.baselineFinish)}</span></FieldRow>
      )}
      {activity.baselineDurationDays != null && (
        <FieldRow label={t('Schedule_field_baseline_duration')}><span className="text-sm tabular-nums">{formatDays(t, activity.baselineDurationDays)}</span></FieldRow>
      )}
      {variance != null && (
        <FieldRow label={t('Schedule_tooltip_variance')}>
          <span className={cn('text-sm tabular-nums', variance > 0 && 'text-destructive')}>
            {variance > 0 ? '+' : ''}{variance} {t('Schedule_unit_days_short')}
          </span>
        </FieldRow>
      )}
      <FieldRow label={t('Schedule_col_health')}><HealthBadge health={activity.health} /></FieldRow>
      {activity.isCritical && (
        <FieldRow label={t('Schedule_critical')}><Badge variant="danger">{t('Schedule_critical')}</Badge></FieldRow>
      )}
      {activity.warningCodes.length > 0 && (
        <FieldRow label={t('Schedule_warnings')}>
          <ul className="list-disc pl-4 text-xs text-amber-700 space-y-0.5">
            {activity.warningCodes.map((code) => <li key={code}>{t(`Schedule_warning_${code}`)}</li>)}
          </ul>
        </FieldRow>
      )}
    </div>
  )
}

export default DatesSection
